import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getOrganizationUsage } from "Api/billingSystem/usage";

import type { OrganizationUsage } from "Api/billingSystem";
import type { RootState } from "Store/configureStore";

// Thunks
export const getOrganizationUsageThunk = createAppAsyncThunk(
  "billingSystem/organizationUsage/getByOrganization",
  async (
    { organizationId }: { organizationId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getOrganizationUsage(organizationId);
    } catch (err) {
      logger(err, {
        action: "get_organization_usage",
        organizationId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type OrganizationUsageState = {
  byOrganizationId: Record<
    string,
    {
      loading: boolean;
      data?: OrganizationUsage;
      error?: string;
    }
  >;
};

const initialState: OrganizationUsageState = {
  byOrganizationId: {}
};

// Slice
const organizationUsageSlice = createSlice({
  name: "organizationUsage",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOrganizationUsageThunk.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byOrganizationId", meta.arg.organizationId, "loading"],
          true
        );
        setDeep(
          state,
          ["byOrganizationId", meta.arg.organizationId, "error"],
          undefined
        );
      })
      .addCase(getOrganizationUsageThunk.fulfilled, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["byOrganizationId", organizationId, "loading"], false);
        setDeep(
          state,
          ["byOrganizationId", organizationId, "data"],
          action.payload
        );
      })
      .addCase(getOrganizationUsageThunk.rejected, (state, action) => {
        const { organizationId } = action.meta.arg;
        setDeep(state, ["byOrganizationId", organizationId, "loading"], false);
        setDeep(
          state,
          ["byOrganizationId", organizationId, "error"],
          action.payload?.error
        );
      });
  }
});

export default organizationUsageSlice.reducer;

// Selectors
const organizationUsageStateSelector = (state: RootState) =>
  state.organizationUsage;

export const selectOrganizationUsage = createSelector(
  organizationUsageStateSelector,
  (_: RootState, organizationId: string) => organizationId,
  (state, organizationId) => state.byOrganizationId[organizationId]?.data
);

export const selectOrganizationUsageLoading = createSelector(
  organizationUsageStateSelector,
  (_: RootState, organizationId: string) => organizationId,
  (state, organizationId) =>
    state.byOrganizationId[organizationId]?.loading ?? false
);

export const selectOrganizationUsageLoaded = createSelector(
  organizationUsageStateSelector,
  (_: RootState, organizationId: string) => organizationId,
  (state, organizationId) => organizationId in state.byOrganizationId
);

export const selectOrganizationUsageError = createSelector(
  organizationUsageStateSelector,
  (_: RootState, organizationId: string) => organizationId,
  (state, organizationId) => state.byOrganizationId[organizationId]?.error
);
